import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { InlineFormattedNumber, NumberVariants, type Column, type ColumnDef, type Leaves } from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import type { MaoRow } from './MaoRow';

interface UseColumnsProps {
  baseCurrency: string;
  quoteCurrency: string;
}

export const useColumns = ({ baseCurrency, quoteCurrency }: UseColumnsProps): { columns: Column[] } => {
  const { canViewCreditAndBalances } = useFeatureFlag();

  const numericColum = (field: Leaves<MaoRow>, currencyField: keyof MaoRow, title: string, hide = false) => {
    return {
      type: 'custom',
      field,
      title,
      params: {
        hide,
        headerClass: 'ag-right-aligned-header',
        cellClass: 'ag-right-aligned-cell',
        cellRenderer: (params: ICellRendererParams) => {
          const currency = params.data?.[currencyField] ?? '';
          const currencyIncrement = params.data?.[`${currencyField}Increment`];
          return params.value && params.value !== '0' ? (
            <InlineFormattedNumber
              variant={params.value < 0 ? NumberVariants.Negative : undefined}
              number={params.value}
              currency={currency}
              increment={currencyIncrement}
            />
          ) : (
            '-'
          );
        },
      },
    } satisfies ColumnDef<MaoRow>;
  };

  const columns = useMemo(() => {
    return compact([
      {
        type: 'text',
        field: 'type',
        hide: true,
        rowGroup: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'text',
        field: 'name',
        width: 200,
        title: 'Name',
        hide: true,
        frozen: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'marketColor',
        field: 'market',
        frozen: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
      },
      {
        type: 'filler',
        id: 'filler',
      },
      canViewCreditAndBalances && numericColum('unsettledBase', 'baseCurrency', `Unsettled ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('unsettledQuote', 'quoteCurrency', `Unsettled ${quoteCurrency}`),
      canViewCreditAndBalances && numericColum('maxBuyBase', 'baseCurrency', `Max Buy ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('maxBuyQuote', 'quoteCurrency', `Max Buy ${quoteCurrency}`, true),
      canViewCreditAndBalances && numericColum('maxSellBase', 'baseCurrency', `Max Sell ${baseCurrency}`),
      canViewCreditAndBalances && numericColum('maxSellQuote', 'quoteCurrency', `Max Sell ${quoteCurrency}`, true),
    ]) satisfies ColumnDef<MaoRow>[];
  }, [baseCurrency, quoteCurrency, canViewCreditAndBalances]);

  return { columns };
};
